<script lang="ts" setup>
import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import VueMultiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import '../../views/Polizas/Polizas.scss';
import ClientsABMRepository, {
  IMinifiedClient,
} from '../../repositories/clientsABM.repository';
import { debounce } from '../../helpers/debounce';
import {
  ApiPolicyCompanyOption,
  ApiPolicyFilters,
} from '../../types/policies.type';

const props = withDefaults(defineProps<{
  typePolicyTransport: string;
  companies: ApiPolicyCompanyOption[];
  showCompany: boolean;
  showDownload?: boolean;
  isDownloadingExcel?: boolean;
}>(), {
  showDownload: true,
  isDownloadingExcel: false,
});

const emit = defineEmits<{
  search: [filters: ApiPolicyFilters];
  reset: [];
  download: [filters: ApiPolicyFilters];
}>();

const { t } = useI18n();
const filters = reactive({
  search: '',
  client: null as IMinifiedClient | null,
  certificateNumber: '',
  startDate: '',
  endDate: '',
  company: null as ApiPolicyCompanyOption | null,
});

const clientSelect = ref<any>(null);
const clientOptions = ref<IMinifiedClient[]>([]);
const clientPage = ref(0);
const clientHasNextPage = ref(true);
const clientLoading = ref(false);
const clientSearch = ref('');
let clientRequestVersion = 0;
let loadedClientSearch: string | null = null;
let clientScrollElement: HTMLElement | null = null;

const getFilters = (): ApiPolicyFilters => ({
  search: filters.search.trim() || undefined,
  clientId: filters.client?.id,
  certificateNumber: filters.certificateNumber.trim() || undefined,
  startDate: filters.startDate || undefined,
  endDate: filters.endDate || undefined,
  company: filters.company?.value ?? undefined,
});

const mergeClients = (clients: IMinifiedClient[]) => {
  const clientsById = new Map(
    clientOptions.value.map((client) => [client.id, client]),
  );
  clients.forEach((client) => clientsById.set(client.id, client));
  clientOptions.value = Array.from(clientsById.values());
};

const loadClients = async (reset: boolean = false) => {
  if (clientLoading.value && !reset) return;
  if (!reset && !clientHasNextPage.value) return;

  if (reset) {
    clientPage.value = 0;
    clientHasNextPage.value = true;
    clientOptions.value = [];
    loadedClientSearch = null;
  }

  const requestVersion = reset
    ? ++clientRequestVersion
    : clientRequestVersion;
  const nextPage = clientPage.value + 1;
  const requestSearch = clientSearch.value;
  clientLoading.value = true;

  try {
    const response = await ClientsABMRepository.getMinifiedClients({
      page: nextPage,
      search: requestSearch,
      typePolicyTransport: props.typePolicyTransport,
    });

    if (requestVersion !== clientRequestVersion) return;

    mergeClients(response.clients);
    clientPage.value = response.pagination.current_page;
    clientHasNextPage.value = response.pagination.has_next_page;
    loadedClientSearch = requestSearch;
  } catch {
    if (requestVersion === clientRequestVersion) {
      clientHasNextPage.value = false;
    }
  } finally {
    if (requestVersion === clientRequestVersion) {
      clientLoading.value = false;
    }
  }
};

const handleClientScroll = () => {
  if (!clientScrollElement) return;

  const reachedBottom = (
    clientScrollElement.scrollTop + clientScrollElement.clientHeight
    >= clientScrollElement.scrollHeight - 10
  );

  if (reachedBottom) {
    loadClients();
  }
};

const removeClientScrollListener = () => {
  clientScrollElement?.removeEventListener('scroll', handleClientScroll);
  clientScrollElement = null;
};

const attachClientScrollListener = async () => {
  await nextTick();
  removeClientScrollListener();
  clientScrollElement = clientSelect.value?.$el
    ?.querySelector('.multiselect__content-wrapper') ?? null;
  clientScrollElement?.addEventListener('scroll', handleClientScroll);
};

const debouncedLoadClients = debounce(() => loadClients(true), 300);

const handleClientOpen = async () => {
  await attachClientScrollListener();
  if (loadedClientSearch !== clientSearch.value) {
    await loadClients(true);
  }
};

const handleClientClose = () => {
  removeClientScrollListener();
  clientSearch.value = '';
  debouncedLoadClients.cancel();
};

const handleClientSearch = (search: string) => {
  clientSearch.value = search;
  debouncedLoadClients();
};

const handleReset = () => {
  filters.search = '';
  filters.client = null;
  filters.certificateNumber = '';
  filters.startDate = '';
  filters.endDate = '';
  filters.company = null;
  emit('reset');
};

onMounted(() => {
  loadClients(true);
});

onBeforeUnmount(() => {
  debouncedLoadClients.cancel();
  removeClientScrollListener();
});
</script>

<template>
  <div class="poliza">
    <form class="poliza__form" @submit.prevent="emit('search', getFilters())">
      <div class="poliza__fields">
        <div class="poliza__field">
          <label class="label-global" for="api-policy-search">
            {{ t('global.buscar') }}
          </label>
          <input
            id="api-policy-search"
            v-model="filters.search"
            class="input-global"
            type="text"
            :placeholder="t('polizas.polizas.buscar_poliza_api')"
          />
        </div>

        <div class="poliza__field">
          <label class="label-global" for="api-policy-client">
            {{ t('global.asegurado') }}
          </label>
          <VueMultiselect
            id="api-policy-client"
            ref="clientSelect"
            v-model="filters.client"
            class="poliza__multiselect"
            :options="clientOptions"
            :loading="clientLoading"
            :internal-search="false"
            :options-limit="Math.max(clientOptions.length, 20)"
            :show-labels="false"
            :allow-empty="true"
            track-by="id"
            label="tradeName"
            :placeholder="t('polizas.polizas.seleccionar_cliente')"
            @search-change="handleClientSearch"
            @open="handleClientOpen"
            @close="handleClientClose"
          >
            <template #noResult>
              {{ t('polizas.polizas.sin_clientes') }}
            </template>
            <template #noOptions>
              {{ t('polizas.polizas.sin_clientes') }}
            </template>
          </VueMultiselect>
        </div>

        <div class="poliza__field">
          <label class="label-global" for="api-certificate-number">
            {{ t('polizas.polizas.numero_de_certificado') }}
          </label>
          <input
            id="api-certificate-number"
            v-model="filters.certificateNumber"
            class="input-global"
            type="text"
            :placeholder="t('polizas.polizas.numero_de_certificado')"
          />
        </div>

        <div class="poliza__field">
          <label class="label-global" for="api-policy-start-date">
            {{ t('polizas.polizas.fecha_de_inicio') }}
          </label>
          <VueDatePicker
            id="api-policy-start-date"
            v-model="filters.startDate"
            class="poliza__datepicker"
            :enable-time-picker="false"
            format="dd/MM/yyyy"
            model-type="yyyy-MM-dd"
            auto-apply
            :placeholder="t('polizas.polizas.fecha_de_inicio')"
          />
        </div>

        <div class="poliza__field">
          <label class="label-global" for="api-policy-end-date">
            {{ t('polizas.polizas.fecha_de_fin') }}
          </label>
          <VueDatePicker
            id="api-policy-end-date"
            v-model="filters.endDate"
            class="poliza__datepicker"
            :enable-time-picker="false"
            format="dd/MM/yyyy"
            model-type="yyyy-MM-dd"
            auto-apply
            :placeholder="t('polizas.polizas.fecha_de_fin')"
          />
        </div>

        <div v-if="showCompany" class="poliza__field">
          <label class="label-global" for="api-policy-company">
            {{ t('global.sponsor') }}
          </label>
          <VueMultiselect
            id="api-policy-company"
            v-model="filters.company"
            class="poliza__multiselect"
            :options="companies"
            :show-labels="false"
            :allow-empty="true"
            track-by="value"
            label="label"
            :placeholder="t('global.elije_una_opcion')"
          />
        </div>
      </div>

      <div class="poliza__actions">
        <button class="background-blue button-global button__form" type="submit">
          {{ t('global.filtrar') }}
        </button>
        <button
          class="background-blue button-global button__form"
          type="button"
          @click="handleReset"
        >
          {{ t('global.limpiar_filtro') }}
        </button>
        <button
          v-if="showDownload"
          class="background-blue button-global button__form position-relative"
          type="button"
          :disabled="isDownloadingExcel"
          @click="emit('download', getFilters())"
        >
          <span
            v-if="isDownloadingExcel"
            class="position-absolute top-50 start-50 translate-middle d-flex"
          >
            <BSpinner small />
          </span>
          <span :class="{ invisible: isDownloadingExcel }">{{ t('global.descargar_excel') }}</span>
        </button>
      </div>
    </form>
  </div>
</template>
